import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { ensurePushSubscription } from "../lib/webPush";

/**
 * Iter 98 — Star toggle to follow/unfollow a team.  Once toggled on,
 * the global LivePushNotifier (registered in App.js) polls
 * /live/now-playing every 60s and fires a browser notification when
 * any followed team appears in the live list.
 *
 * Pure client-side polling — no Web Push server required.  Works as
 * long as the user has the tab open.
 */
const FollowTeamButton = ({ teamId, teamName, size = "md" }) => {
  const { token, user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token || !teamId || !user) return;
    axios
      .get(`${API}/me/followed-teams`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const teams = r.data?.teams || [];
        setFollowing(teams.some((t) => t.team_id === teamId));
      })
      .catch(() => setFollowing(false));
  }, [token, teamId, user]);

  const handleToggle = async (e) => {
    e?.stopPropagation();
    if (!token || !user) {
      toast.info("Log in to follow teams and get live alerts");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      if (following) {
        await axios.delete(`${API}/me/followed-teams/${teamId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFollowing(false);
        toast.success(`Unfollowed ${teamName}`);
      } else {
        await axios.post(
          `${API}/me/followed-teams`,
          { team_id: teamId, team_name: teamName },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setFollowing(true);
        // Request notification permission on first follow + register Web Push
        if ("Notification" in window && Notification.permission === "default") {
          await Notification.requestPermission();
        }
        ensurePushSubscription(token).catch(() => {});
        toast.success(`Following ${teamName} — you'll get a live alert when they kick off`);
      }
    } catch (err) {
      toast.error("Couldn't update — try again");
    } finally {
      setBusy(false);
    }
  };

  const dim = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-5 h-5" : "w-4 h-4";

  return (
    <button
      onClick={handleToggle}
      disabled={busy}
      data-testid={`follow-team-${teamId}`}
      aria-label={following ? `Unfollow ${teamName}` : `Follow ${teamName}`}
      className="inline-flex items-center justify-center p-1 rounded-full hover:bg-muted/40 transition-colors disabled:opacity-40"
    >
      <Star
        className={`${dim} transition-colors ${
          following
            ? "text-amber-500 fill-amber-500"
            : "text-muted-foreground hover:text-amber-500"
        }`}
      />
    </button>
  );
};

export default FollowTeamButton;
